import { PATHS } from '../config';
import { dynoapeAPI } from '../dynoape';
import type { Employee, Pattern } from './Models';

const getEmployee = async (
    departmentId: string,
    employeeId: string,
): Promise<Employee> =>
    await dynoapeAPI.get(PATHS.employee(departmentId, employeeId));

/*
 * patternsQuery optionally selects the planner's all-patterns projection
 * (PatternCase.ALL_PATTERNS) — a different server-side payload, so it
 * forms a separate cache entry in the same cache store.
 */
const getEmployees = async (
    departmentId: string,
    patternsQuery = '',
): Promise<Employee[]> =>
    await dynoapeAPI.get(`${PATHS.employees(departmentId)}${patternsQuery}`);

const createEmployee = async (
    departmentId: string,
    employee: Employee,
): Promise<Employee> =>
    await dynoapeAPI.post(PATHS.employeeCreate(departmentId), employee);

/*
 * Partial payload: the weekend planner sends a
 * deliberately partial update (spread minus planner-managed fields)
 */
const updateEmployee = async (
    departmentId: string,
    employeeId: string,
    employee: Partial<Employee>,
): Promise<Employee> =>
    await dynoapeAPI.put(PATHS.employee(departmentId, employeeId), employee);

/*
 * Merges a partial employee update (PATCH, e.g. the holiday planner's
 * holiday rules). emPatch swallows HTTP errors and resolves undefined
 * — the axios interceptor has already shown the error toast.
 */
const patchEmployee = async (
    departmentId: string,
    employeeId: string,
    patch: Partial<Employee>,
): Promise<Employee | undefined> =>
    await dynoapeAPI.emPatch(PATHS.employee(departmentId, employeeId), patch);

const createEmployeePattern = async (
    departmentId: string,
    employeeId: string,
    patternCase: string,
    pattern: Pattern,
): Promise<Pattern> =>
    await dynoapeAPI.post(
        PATHS.employeePattern(departmentId, employeeId, patternCase),
        pattern,
    );

const updateEmployeePattern = async (
    departmentId: string,
    employeeId: string,
    patternId: string,
    patternCase: string,
    pattern: Pattern,
): Promise<Pattern> =>
    await dynoapeAPI.put(
        PATHS.employeePatternById(
            departmentId,
            employeeId,
            patternId,
            patternCase,
        ),
        pattern,
    );

/*
 * `?? true`: DELETE responds with an empty body — the marker gives
 * consumers a truthy completion signal. Failed requests throw.
 */
const deleteEmployeePattern = async (
    departmentId: string,
    employeeId: string,
    patternId: string,
    patternCase: string,
): Promise<true> =>
    (await dynoapeAPI.delete(
        PATHS.employeePatternById(
            departmentId,
            employeeId,
            patternId,
            patternCase,
        ),
    )) ?? true;

/*
 * Links an employee to an org employee by setting the external employee
 * id on the employee record. The response carries the updated employee
 * (e.g. email)
 */
const updateEmployeeExtId = async (
    departmentId: string,
    employeeId: string,
    value: string,
): Promise<Employee> =>
    await dynoapeAPI.put(PATHS.employeeExtId(departmentId, employeeId), {
        value,
    });

/*
 * The API wraps response bodies in a `{ body }` envelope, which mutating
 * endpoints may omit (e.g. DELETE responds with an empty body). Consumers
 * that detect completion through the useApiCall response get a fallback
 * value instead, so a successfully resolved request is always reflected
 * in the response. Failed requests throw and leave the response untouched.
 */
const deleteEmployee = async (
    departmentId: string,
    employeeId: string,
): Promise<true> =>
    (await dynoapeAPI.delete(PATHS.employee(departmentId, employeeId))) ?? true;

const EmployeeService = {
    getEmployee,
    getEmployees,
    createEmployee,
    updateEmployee,
    patchEmployee,
    createEmployeePattern,
    updateEmployeePattern,
    deleteEmployeePattern,
    updateEmployeeExtId,
    deleteEmployee,
};

export default EmployeeService;
